<template>
  <!-- Desktop Section: Initially Black, Gradient on Scroll -->
  <div class="overflow-hidden">
    <!-- Loading bar at the bottom -->
    <div v-if="isPreloading" class="fixed bottom-0 left-0 w-full z-[9999]">
      <div class="relative w-full h-[27px] bg-[#ffffff]">
        <div
          class="absolute left-0 top-0 h-[27px] bg-[#52DA75] transition-all duration-300"
          :style="{ width: loadingProgress + '%' }"
        ></div>
        <span
          class="absolute top-1/2 -translate-y-1/2 text-xs text-[#161816] font-semibold transition-all duration-300"
          :style="{
            left: `calc(${loadingProgress}% - 48px)`,
            minWidth: '60px',
            textAlign: 'right'
          }"
        >
          Loading {{ Math.round(loadingProgress) }}%
        </span>
      </div>
    </div>

    <section
      class="desktop-section hidden lg:grid grid-cols-8 items-center min-h-screen justify-between py-[4rem]"
      style="background: #161816;"
    >
      <!-- Left Side with Hidden Scroll -->
      <div class="left-side-container col-start-1 col-span-3">
      <div class="flex -mt-[10%]">
        <div class="flex flex-col gap-y-6 mt-20">
            <div
              v-for="(video, idx) in leftCol3"
              :key="video.id"
              :class="[
                'group relative rounded-xl overflow-hidden bg-[#222] shadow-md cursor-pointer w-auto h-[299px] transition-transform duration-300 hover:scale-105 hover:shadow-2xl creator-video-anim',
                !loadedImages.has(video.id) ? 'px-10' : ''
              ]"
                            @click="openVideo(idx)"
            >
              <div v-if="!loadedImages.has(video.id)" class="absolute inset-0 flex items-center justify-center bg-black/50">
                <div class="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
              <img
                :src="video.thumbnail"
                class="w-full h-full object-cover pointer-events-none transition-all duration-300 group-hover:brightness-110 group-hover:contrast-125 opacity-0"
                :alt="video.title"
                @load="(e) => {
                  const img = e.target as HTMLImageElement;
                  img.classList.remove('opacity-0');
                }"
              />
            </div>
          </div>
          <div class="flex flex-col gap-y-6 ml-4 !-mt-20">
            <div
              v-for="(video, idx) in leftCol4"
              :key="video.id"
              :class="[
                'group relative rounded-xl overflow-hidden bg-[#222] shadow-md cursor-pointer w-auto h-[299px] transition-transform duration-300 hover:scale-105 hover:shadow-2xl creator-video-anim',
                !loadedImages.has(video.id) ? 'px-10' : ''
              ]"              @mouseenter="playVideo($event)"
              @mouseleave="pauseVideo($event)"
              @click="openVideo(idx + 3)"
            >
              <div v-if="!loadedImages.has(video.id)" class="absolute inset-0 flex items-center justify-center bg-black/50">
                <div class="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
              <img
                :src="video.thumbnail"
                class="w-full h-full object-cover pointer-events-none transition-all duration-300 group-hover:brightness-110 group-hover:contrast-125 opacity-0"
                :alt="video.title"
                @load="(e) => {
                  const img = e.target as HTMLImageElement;
                  img.classList.remove('opacity-0');
                }"
              />
            </div>
          </div>
          <div class="flex flex-col gap-y-6 mt-20 ml-4">
            <div
              v-for="(video, idx) in left2Col4"
              :key="video.id"
              :class="[
                'group relative rounded-xl overflow-hidden bg-[#222] shadow-md cursor-pointer w-auto h-[299px] transition-transform duration-300 hover:scale-105 hover:shadow-2xl creator-video-anim',
                !loadedImages.has(video.id) ? 'px-10' : ''
              ]"            
              @click="openVideo(idx + 7)"
            >
              <div v-if="!loadedImages.has(video.id)" class="absolute inset-0 flex items-center justify-center bg-black/50">
                <div class="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
              <img
                :src="video.thumbnail"
                class="w-full h-full object-cover pointer-events-none transition-all duration-300 group-hover:brightness-110 group-hover:contrast-125 opacity-0"
                :alt="video.title"
                @load="(e) => {
                  const img = e.target as HTMLImageElement;
                  img.classList.remove('opacity-0');
                }"
              />
            </div>
          </div>
      </div>
      </div>

      <!-- Center Text (Fixed) -->
      <div class="center-text flex flex-col items-center justify-center col-start-4 col-span-2 w-full">
        <span class="text-[#161816] text-[48px] leading-[48px] xl:text-[80px] font-[500] text-center xl:leading-[80px]">6000+ </span>
        <span class="text-[#161816] text-[48px] leading-[48px] xl:text-[80px] font-[500] text-center xl:leading-[80px]">Winning</span>
        <span class="text-[#161816] text-[48px] leading-[48px] xl:text-[80px] font-[500] text-center xl:leading-[80px]">Creators.</span>
      </div>

      <!-- Right Side with Hidden Scroll -->
      <div class="right-side-container col-start-6 col-span-3">
      <div class="flex justify-end -mt-[10%]">
        <div class="flex flex-col gap-y-6 mt-20 mr-4">
          <div
              v-for="(video, idx) in rightCol4"
              :key="video.id"
              :class="[
                'group relative rounded-xl overflow-hidden bg-[#222] shadow-md cursor-pointer w-auto h-[299px] transition-transform duration-300 hover:scale-105 hover:shadow-2xl creator-video-anim',
                !loadedImages.has(video.id) ? 'px-10' : ''
              ]"            
              @click="openVideo(idx + 10)"
          >
            <div v-if="!loadedImages.has(video.id)" class="absolute inset-0 flex items-center justify-center bg-black/50">
              <div class="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
            <img
              :src="video.thumbnail"
              class="w-full h-full object-cover pointer-events-none transition-all duration-300 group-hover:brightness-110 group-hover:contrast-125 opacity-0"
              :alt="video.title"
              @load="(e) => {
                const img = e.target as HTMLImageElement;
                img.classList.remove('opacity-0');
              }"
            />
          </div>
        </div>
        <div class="flex flex-col gap-y-6 !-mt-20">
          <div
              v-for="(video, idx) in rightCol3"
              :key="video.id"
              :class="[
                'group relative rounded-xl overflow-hidden bg-[#222] shadow-md cursor-pointer w-auto h-[299px] transition-transform duration-300 hover:scale-105 hover:shadow-2xl creator-video-anim',
                !loadedImages.has(video.id) ? 'px-10' : ''
              ]"      
              @click="openVideo(idx + 13)"
          >
            <div v-if="!loadedImages.has(video.id)" class="absolute inset-0 flex items-center justify-center bg-black/50">
              <div class="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
            <img
              :src="video.thumbnail"
              class="w-full h-full object-cover pointer-events-none transition-all duration-300 group-hover:brightness-110 group-hover:contrast-125 opacity-0"
              :alt="video.title"
              @load="(e) => {
                const img = e.target as HTMLImageElement;
                img.classList.remove('opacity-0');
              }"
            />
          </div>
        </div>
        <div class="flex flex-col gap-y-6 mt-20 ml-4">
          <div
              v-for="(video, idx) in right2Col4"
              :key="video.id"
              :class="[
                'group relative rounded-xl overflow-hidden bg-[#222] shadow-md cursor-pointer w-auto h-[299px] transition-transform duration-300 hover:scale-105 hover:shadow-2xl creator-video-anim',
                !loadedImages.has(video.id) ? 'px-10' : ''
              ]"            
              @click="openVideo(idx + 17)"
          >
            <div v-if="!loadedImages.has(video.id)" class="absolute inset-0 flex items-center justify-center bg-black/50">
              <div class="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
            <img
              :src="video.thumbnail"
              class="w-full h-full object-cover pointer-events-none transition-all duration-300 group-hover:brightness-110 group-hover:contrast-125 opacity-0"
              :alt="video.title"
              @load="(e) => {
                const img = e.target as HTMLImageElement;
                img.classList.remove('opacity-0');
              }"
            />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>

  <!-- Mobile Section: 3-column custom grid, GIF previews, 8 videos only -->
  <section class="lg:hidden bg-[#161816] text-white py-8">
    <h2 class="text-[30px] font-[500] mx-5">6000+ winning creators.</h2>
    <div class="relative w-full flex flex-row justify-center gap-2 px-4 pb-4 mt-4 min-h-[750px]">
      <!-- First column: 3 videos, top-aligned -->
      <div class="flex flex-col gap-4 justify-end" style="flex:1;">
        <div v-for="(video, _) in mobileColumns[0]" :key="video.id" class="rounded-xl overflow-hidden bg-[#222] shadow-md cursor-pointer w-full h-[180px] flex-shrink-0" @click="openVideo(video.globalIdx)">
          <div v-if="!loadedGifPreviews.has(video.id)" class="absolute inset-0 flex items-center justify-center bg-black/50">
            <div class="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <img v-else :src="video.gifPreview || video.thumbnail" class="w-full h-full object-cover pointer-events-none" :alt="video.title" />
        </div>
      </div>
      <!-- Second column: 3 videos, shifted down -->
      <div class="flex flex-col gap-4 justify-start" style="flex:1; margin-top:60px;">
        <div v-for="(video, _) in mobileColumns[1]" :key="video.id" class="rounded-xl overflow-hidden bg-[#222] shadow-md cursor-pointer w-full h-[180px] flex-shrink-0" @click="openVideo(video.globalIdx)">
          <div v-if="!loadedGifPreviews.has(video.id)" class="absolute inset-0 flex items-center justify-center bg-black/50">
            <div class="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <img v-else :src="video.gifPreview || video.thumbnail" class="w-full h-full object-cover pointer-events-none" :alt="video.title" />
        </div>
      </div>
      <!-- Third column: 2 videos, centered vertically -->
      <div class="flex flex-col gap-4 justify-center" style="flex:1; min-height:540px;">
        <div v-for="(video, _) in mobileColumns[2]" :key="video.id" class="rounded-xl overflow-hidden bg-[#222] shadow-md cursor-pointer w-full h-[180px] flex-shrink-0" @click="openVideo(video.globalIdx)">
          <div v-if="!loadedGifPreviews.has(video.id)" class="absolute inset-0 flex items-center justify-center bg-black/50">
            <div class="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <img v-else :src="video.gifPreview || video.thumbnail" class="w-full h-full object-cover pointer-events-none" :alt="video.title" />
        </div>
      </div>
    </div>
  </section>

  <!-- Fullscreen Modal (always present, works for both) -->
  <div v-if="activeIndex !== null" class="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center">
    <div class="relative w-full max-w-xs aspect-[9/16] flex flex-col items-center bg-[#161816] rounded-xl">
      <div v-if="isLoadingFullVideo" class="absolute inset-0 flex items-center justify-center bg-black/50 rounded-t-xl">
        <div class="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
      <video
        v-if="fullVideo"
        :src="fullVideo.fullVideoUrl"
        autoplay
        loop
        :poster="fullVideo.thumbnail"
        class="w-full h-full object-cover rounded-t-xl"
      ></video>
    </div>
    <button
      class="mt-6 w-11/12 max-w-xs py-3 text-base font-semibold bg-[#222] text-white rounded-lg"
      @click="closeVideo"
    >Close</button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, inject } from 'vue'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { useCreatorStore } from '../stores/creatorStore'
import type { CreatorVideo } from '../services/datocms'
import { getFullVideo } from '../services/datocms'

const navbarTransparent = inject('navbarTransparent') as { value: boolean }
let sectionObserver: IntersectionObserver | null = null;

const creatorStore = useCreatorStore()

const activeIndex = ref<number | null>(null)
const fullVideo = ref<CreatorVideo | null>(null)
const isLoadingFullVideo = ref(false)
const loadedImages = ref<Set<string>>(new Set())
const isPreloading = ref(true)
const loadingProgress = ref(0)
const loadingText = ref('0%')
const isAnimating = ref(false)
const isReversing = ref(false)
let observer: IntersectionObserver | null = null;
let scrollTrigger: ScrollTrigger | null = null;
let isFirstLoad = true;
let isVisible = ref(true);

// Preload all images in parallel
const preloadImages = async (videos: CreatorVideo[]) => {
  const totalImages = videos.length
  let loadedCount = 0

  const preloadPromises = videos.map(video => {
    return new Promise((resolve) => {
      if (!video.thumbnail || loadedImages.value.has(video.id)) {
        loadedCount++
        loadingProgress.value = (loadedCount / totalImages) * 100
        loadingText.value = `${Math.round(loadingProgress.value)}%`
        resolve(null)
        return
      }

      const img = new Image()
      img.onload = () => {
        loadedImages.value.add(video.id)
        loadedCount++
        loadingProgress.value = (loadedCount / totalImages) * 100
        loadingText.value = `${Math.round(loadingProgress.value)}%`
        resolve(null)
      }
      img.onerror = () => {
        console.error(`Failed to load thumbnail for video ${video.id}`)
        loadedCount++
        loadingProgress.value = (loadedCount / totalImages) * 100
        loadingText.value = `${Math.round(loadingProgress.value)}%`
        resolve(null)
      }
      img.src = video.thumbnail
    })
  })

  await Promise.all(preloadPromises)
  isPreloading.value = false
}

const openVideo = async (idx: number) => {
  activeIndex.value = idx
  isLoadingFullVideo.value = true
  document.body.style.overflow = 'hidden'
  
  try {
    const video = creatorStore.videos[idx]
    const fullVideoData = await getFullVideo(video.id)
    if (fullVideoData) {
      fullVideo.value = fullVideoData
    }
  } catch (error) {
    console.error('Error loading full video:', error)
  } finally {
    isLoadingFullVideo.value = false
  }
}

const closeVideo = () => {
  activeIndex.value = null
  fullVideo.value = null
  document.body.style.overflow = ''
}

// For desktop columns
const leftCol3 = computed(() => creatorStore.videos.slice(0, 3))
const leftCol4 = computed(() => creatorStore.videos.slice(3, 7))
const left2Col4 = computed(() => creatorStore.videos.slice(7, 10))
const rightCol4 = computed(() => creatorStore.videos.slice(10, 13))
const rightCol3 = computed(() => creatorStore.videos.slice(13, 17))
const right2Col4 = computed(() => creatorStore.videos.slice(17, 20))

// Only first 8 videos for mobile, split into 3 columns for custom layout
const mobileColumns = computed(() => {
  const videos = creatorStore.videos.slice(0, 8).map((video, idx) => ({ ...video, globalIdx: idx }))
  // First col: 3 videos (0,1,2), second col: 3 videos (3,4,5), third col: 2 videos (6,7)
  return [
    videos.slice(0, 3),
    videos.slice(3, 6),
    videos.slice(6, 8)
  ]
})

// Preload GIF previews for mobile
const loadedGifPreviews = ref<Set<string>>(new Set())
const preloadGifPreviews = async (videos: CreatorVideo[]) => {
  const promises = videos.slice(0, 8).map(video => {
    return new Promise<void>((resolve) => {
      if (!video.gifPreview) {
        loadedGifPreviews.value.add(video.id)
        resolve()
        return
      }
      const img = new window.Image()
      img.onload = () => {
        loadedGifPreviews.value.add(video.id)
        resolve()
      }
      img.onerror = () => {
        loadedGifPreviews.value.add(video.id)
        resolve()
      }
      img.src = video.gifPreview
    })
  })
  await Promise.all(promises)
}

const playVideo = (event: MouseEvent) => {
  const video = (event.currentTarget as HTMLElement).querySelector('video');
  if (video) {
    video.currentTime = 0;
    video.play();
  }
}

const pauseVideo = (event: MouseEvent) => {
  const video = (event.currentTarget as HTMLElement).querySelector('video');
  if (video) {
    video.pause();
    video.currentTime = 0;
  }
}

// Add this function to handle animation state
const resetAnimationState = () => {
  gsap.set('.desktop-section', { 
    opacity: 1,
    background: '#161816',
  });
  gsap.set('.left-side-container .flex-col, .right-side-container .flex-col', {
    y: "100%",
    opacity: 0
  });
  gsap.set('.center-text', { 
    opacity: 0,
    scale: 0.8,
    y: "100%",
    zIndex: 2
  });
};

// Add this function to handle forward animation
const playForwardAnimation = () => {
  if (isAnimating.value || isReversing.value) return;
  isAnimating.value = true;
  isReversing.value = false;
  isVisible.value = true;

  // Kill any existing animations
  gsap.killTweensOf('.desktop-section');
  gsap.killTweensOf('.left-side-container .flex-col');
  gsap.killTweensOf('.right-side-container .flex-col');
  gsap.killTweensOf('.center-text');

  // Reset positions first
  resetAnimationState();

  // Step 1: Show site background and gradient
  gsap.to('.desktop-section', {
    opacity: 1,
    background: 'linear-gradient(0deg, rgba(22, 24, 22, 0) 17.28%, #161816 100%), linear-gradient(133.97deg, #52E475 12.48%, #3CDFF8 54.25%, rgba(226, 72, 206, 0.2) 92.81%)',
    duration: 0.7,
    ease: 'power2.inOut',
    onComplete: () => {
      // Step 2: Animate in the center text from bottom
      gsap.to('.center-text', {
        opacity: 1,
        scale: 1,
        y: 0,
        zIndex: 10,
        duration: 0.5,
        ease: 'power2.out',
        onComplete: () => {
          // Step 3: Animate in video columns from bottom
          gsap.to('.left-side-container .flex-col:first-child', { y: 0, opacity: 1, duration: 4, ease: 'power3.out', delay: 0.2 });
          gsap.to('.left-side-container .flex-col:nth-child(2)', { y: 0, opacity: 1, duration: 3.2, ease: 'power3.out', delay: 0.1 });
          gsap.to('.left-side-container .flex-col:nth-child(3)', { y: 0, opacity: 1, duration: 3, ease: 'power3.out' });
          gsap.to('.right-side-container .flex-col:first-child', { y: 0, opacity: 1, duration: 4, ease: 'power3.out', delay: 0.15 });
          gsap.to('.right-side-container .flex-col:nth-child(2)', { y: 0, opacity: 1, duration: 5, ease: 'power3.out', delay: 0.35 });
          gsap.to('.right-side-container .flex-col:nth-child(3)', { y: 0, opacity: 1, duration: 4.9, ease: 'power3.out', delay: 0.25 });
          
          isAnimating.value = false;
        }
      });
    }
  });
};

// Add this function to handle reverse animation
const playReverseAnimation = () => {
  if (isReversing.value || isAnimating.value) return;
  isReversing.value = true;
  isAnimating.value = false;

  // Kill any existing animations
  gsap.killTweensOf('.desktop-section');
  gsap.killTweensOf('.left-side-container .flex-col');
  gsap.killTweensOf('.right-side-container .flex-col');
  gsap.killTweensOf('.center-text');

  // First reverse the video columns to top
  gsap.to('.left-side-container .flex-col, .right-side-container .flex-col', {
    y: "-100%",
    opacity: 0,
    duration: 1.5,
    ease: 'power3.in',
    onComplete: () => {
      // Then reverse the center text to top
      gsap.to('.center-text', {
        opacity: 0,
        scale: 0.8,
        y: "-100%",
        duration: 0.5,
        ease: 'power2.in',
        onComplete: () => {
          // Finally reverse the gradient
          gsap.to('.desktop-section', {
            background: '#161816',
            duration: 0.7,
            ease: 'power2.in',
            onComplete: () => {
              isReversing.value = false;
              isVisible.value = false;
            }
          });
        }
      });
    }
  });
};

onMounted(async () => {
  // Fetch videos from DatoCMS
  await creatorStore.fetchVideos()
  // Preload all thumbnails in parallel
  await preloadImages(creatorStore.videos)
  // Preload GIF previews for mobile
  await preloadGifPreviews(creatorStore.videos)

  if (window.innerWidth >= 1024) {
    // Set initial state
    resetAnimationState();

    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);

    // Use IntersectionObserver to trigger animation only when section is visible
    observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (isFirstLoad) {
            isFirstLoad = false;
            playForwardAnimation();
          } else if (!isAnimating.value && !isReversing.value && !isVisible.value) {
            playForwardAnimation();
          }

          // Create ScrollTrigger only if it doesn't exist
          if (!scrollTrigger) {
            scrollTrigger = ScrollTrigger.create({
              trigger: '.desktop-section',
              start: 'top top',
              end: '+=100px',
              pin: true,
              pinSpacing: true,
              onUpdate: (self) => {
                if (self.progress > 0.99 && !isReversing.value && isVisible.value) {
                  playReverseAnimation();
                }
              },
              onEnterBack: () => {
                if (!isAnimating.value && !isReversing.value && !isVisible.value) {
                  playForwardAnimation();
                }
              }
            });
          }
        } else {
          // If section is not intersecting and not already hidden, trigger reverse animation
          if (isVisible.value && !isReversing.value && !isAnimating.value) {
            playReverseAnimation();
          }
        }
      });
    }, { 
      threshold: [0, 0.1, 0.5, 0.9, 1],
      rootMargin: '50px 0px'
    });

    // Start observing the section
    const section = document.querySelector('.desktop-section');
    if (section) {
      observer.observe(section);
    }
  }

  // Create observer for the entire section
  sectionObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        navbarTransparent.value = false;
      } else {
        navbarTransparent.value = true;
      }
    });
  }, { threshold: 0.2 });

  // Start observing the section
  const section = document.querySelector('.desktop-section');
  if (section) {
    sectionObserver.observe(section);
  }
  
  // Cleanup
  onBeforeUnmount(() => {
    if (scrollTrigger) {
      scrollTrigger.kill();
    }
    ScrollTrigger.getAll().forEach(trigger => trigger.kill())
    if (observer) observer.disconnect();
    if (sectionObserver) {
      sectionObserver.disconnect();
    }
    navbarTransparent.value = true;
  })
})

// Add this CSS for smooth animation
const style = document.createElement('style');
style.textContent = `
  .marquee-wrapper {
    position: relative;
    will-change: transform;
    display: flex;
    flex-direction: column;
    transform: translateZ(0);
    backface-visibility: hidden;
    gap: 1.5rem;
  }
  
  .left-side-container .flex-col,
  .right-side-container .flex-col {
    will-change: transform;
    transform: translateZ(0);
    backface-visibility: hidden;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .left-side-container,
  .right-side-container {
    overflow: hidden;
    position: relative;
    height: 100vh;
    transform: translateZ(0);
    backface-visibility: hidden;
  }

  .marquee-wrapper .flex-col {
    flex-shrink: 0;
  }

  .marquee-wrapper .flex-col > div {
    width: auto;
    height: 299px;
    margin-bottom: 0;
    transform: translateZ(0);
    backface-visibility: hidden;
  }

  .left-side-container .flex,
  .right-side-container .flex {
    gap: 1rem;
  }

  .left-side-container .flex-col,
  .right-side-container .flex-col {
    margin: 0;
  }
`;
document.head.appendChild(style);
</script>

<style scoped>
.desktop-section {
  position: relative;
  height: auto;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  transition: background 0.5s ease;
}

.videos-scroll-area {
  position: absolute;
  top: 0; left: 0; width: 100%;
  height: auto;
  min-height: 200vh; /* or calc based on video count for enough scroll */
  will-change: transform;
  display: flex;
  justify-content: space-between;
}

.left-side-container, .right-side-container {
  position: relative;
  height: auto;
  overflow: visible;
}
.left-side-container {
  margin-left: -10%;
}
.right-side-container {
  margin-right: -10%;
}

.center-text {
  position: fixed;
  /* top: 50%;
  left: 50%;
  transform: translate(-50%, -50%); */
  /* width: 100vw; */
  pointer-events: none;
  z-index: 10;
  text-align: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.creator-video-anim {
  will-change: transform, opacity;
}

.creators-section {
  background: #161816;
  color: #fff;
  padding: 2rem 0;
}
.creators-title {
  font-size: 1.3rem;
  margin-left: 1rem;
  margin-bottom: 1.5rem;
}

/* Masonry columns */
.creators-masonry {
  column-count: 3;
  column-gap: 1.2rem;
  max-width: 370px;
  margin: 0 auto;
  padding: 0 1rem;
}
@media (max-width: 600px) {
  .creators-masonry {
    column-count: 2;
    column-gap: 0.7rem;
    max-width: 260px;
    padding: 0 0.5rem;
  }
}
.creator-video-thumb {
  width: 100%;
  margin-bottom: 1.2rem;
  border-radius: 1.2rem;
  overflow: hidden;
  background: #222;
  cursor: pointer;
  box-shadow: 0 2px 8px #0005;
  display: block;
  break-inside: avoid;
  transition: transform 0.2s;
}
.creator-video-thumb:active {
  transform: scale(0.97);
}
.thumb-video {
  width: 100%;
  display: block;
  object-fit: cover;
  aspect-ratio: 9/16;
  pointer-events: none;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.92);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.modal-content {
  position: relative;
  width: 100vw;
  max-width: 420px;
  aspect-ratio: 9/16;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #161816;
  border-radius: 1.2rem;
  padding-bottom: 2.5rem;
}
.modal-video {
  width: 100%;
  height: 100%;
  border-radius: 1.2rem 1.2rem 0 0;
  background: #000;
  object-fit: cover;
}
.close-btn {
  margin-top: 1.2rem;
  width: 90%;
  max-width: 320px;
  padding: 0.7rem 0;
  font-size: 1.1rem;
  color: #fff;
  background: #222;
  border: none;
  border-radius: 0.7rem;
  cursor: pointer;
  font-weight: 500;
  letter-spacing: 0.03em;
  transition: background 0.2s;
}
.close-btn:active {
  background: #333;
}
@media (max-width: 600px) {
  .modal-content {
    max-width: 100vw;
    border-radius: 0;
    padding-bottom: 2.5rem;
  }
  .modal-video {
    border-radius: 0;
  }
}

.creators-horizontal-scroll {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.creators-horizontal-scroll::-webkit-scrollbar {
  display: none;
}


.creators-desktop-layout {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  gap: 2rem;
  position: relative;
}

.side-group {
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
}

.video-col {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.col-3 { margin-top: 80px; }
.col-4 { margin-top: -40px; margin-left: 16px; }
.col-4.right { margin-right: 16px; }

.center-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  z-index: 2;
}

.center-videos {
  margin-right: -60px;
  margin-left: -60px;
  z-index: 1;
}

.center-title {
  text-align: center;
  font-size: 2.5rem;
  font-weight: bold;
  margin: 0 2rem;
  color: #161816;
  z-index: 2;
  background: transparent;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

img {
  transition: opacity 0.3s ease;
}

circle {
  transform-origin: center;
  transform: rotate(-90deg);
  transition: stroke-dashoffset 0.3s ease;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
